import { memo, useContext } from "react"
import styled from "styled-components"
import { FullRow } from "components/styled/Grid"
import Arrow from "components/Slider/Arrow"
import { Viewport } from "components/styled/SliderBase"
import { mq } from "helpers/styled"
import { useEmblaArrows } from "hooks/useEmbla"
import { SliderContext } from "scenes/CarScene/Header/SliderContext"
import dynamic from "next/dynamic"
import { CarContext } from "../CarContext"
import ImageWithFallback from "../../../components/ImageWithFallback"

const PanoramaSlide = dynamic(() => import("scenes/CarScene/CarImages/PanoramaSlide"))
const VideoSlide = dynamic(() => import("scenes/CarScene/CarImages/VideoSlide"))

const Container = styled(FullRow)`
  position: relative;
  max-width: 100vw;
  width: calc(var(--container-padding) * 2 + 100%);
  margin-bottom: 15px;
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ sold }) => sold && `opacity: 0.5`};
  ${({ mediaActive }) => mediaActive && `opacity: 0`};

  svg {
    color: ${({ theme }) => theme.colors.silver};
    right: 0;
    left: 0;
  }

  > div {
    max-width: 720px;
  }

  @media ${mq("lg")} {
    width: 100%;
    margin: 0;
    margin-bottom: 15px;
  }
`

const ControlsContainer = styled.div`
  > :first-child {
    left: 10px;
  }

  > :last-child {
    right: 10px;
  }
`

const Track = styled.div`
  display: flex;
  align-items: center;
`

const Slide = styled.div`
  position: relative;
  min-width: 100%;

  > span {
    --container-padding: 0;
  }
`

function CarImages() {
  const { car, showMedia, extraImages } = useContext(CarContext)
  const { embla, emblaRef } = useContext(SliderContext)
  const {
    brand,
    model,
    version,
    sold,
    sellingStatus,
    frontImage,
    imageRear,
    imageInterior,
    imageInterior2,
    imageExterior,
    youtubeId,
    panoramaConfig
  } = car

  const { prevProps, nextProps } = useEmblaArrows(embla)

  const cloudinaryLoader = ({ src, width, quality }) => {
    const normalizedSrc = src?.replace(/^\/+/, "")
    const optimizedWidth = Math.min(width, 1080)
    return `https://res.cloudinary.com/dag3rmkxg/image/upload/f_auto,c_limit,w_${optimizedWidth},q_${
      quality || 75
    }/${normalizedSrc}`
  }

  return (
    <Container sold={sold} mediaActive={showMedia === "panorama"}>
      <ControlsContainer>
        <Arrow side="left" {...prevProps} />
        <Arrow side="right" {...nextProps} />
      </ControlsContainer>
      <Viewport ref={emblaRef}>
        <Track>
          <Slide>
            <ImageWithFallback
              height={480}
              width={720}
              alt={`Renting ${brand}-${model}-${version}`}
              src={frontImage}
              priority
              fetchpriority="high"
              decoding="sync"
              loading="eager"
              loader={cloudinaryLoader}
              sizes="(min-width: 768px) 100vw, 390px"
            />
          </Slide>
          <Slide>
            <ImageWithFallback
              height={480}
              width={720}
              alt={`Renting ${brand}-${model}-${version}-rear`}
              src={imageRear}
              fetchpriority="low"
              decoding="async"
              loading="lazy"
            />
          </Slide>
          {sellingStatus === "new" && (
            <Slide>
              <ImageWithFallback
                height={480}
                width={720}
                alt={`Renting ${brand}-${model}-${version}-exterior`}
                src={imageExterior}
                fetchpriority="low"
                decoding="async"
                loading="lazy"
                sizes="(min-width: 768px) 100vw, 390px"
              />
            </Slide>
          )}
          <Slide>
            <ImageWithFallback
              height={480}
              width={720}
              alt={`Renting ${brand}-${model}-${version}-interior`}
              src={imageInterior}
              fetchpriority="low"
              decoding="async"
              loading="lazy"
              sizes="(min-width: 768px) 100vw, 390px"
            />
          </Slide>
          {sellingStatus === "new" && (
            <Slide>
              <ImageWithFallback
                height={480}
                width={720}
                alt={`Renting ${brand}-${model}-${version}-interior2`}
                src={imageInterior2}
                fetchpriority="low"
                decoding="async"
                loading="lazy"
                sizes="(min-width: 768px) 100vw, 390px"
              />
            </Slide>
          )}
          {extraImages.map((file, i) => (
            <Slide key={file}>
              <ImageWithFallback
                height={480}
                width={720}
                alt={`Renting ${brand}-${model}-${version}-${i}`}
                src={`cars/${car.id}/${file}`}
                fetchpriority="low"
                decoding="async"
                loading="lazy"
                sizes="(min-width: 768px) 100vw, 390px"
              />
            </Slide>
          ))}
          {panoramaConfig && panoramaConfig.enabled && (
            <Slide id="panorama">
              <PanoramaSlide />
            </Slide>
          )}
          {youtubeId && (
            <Slide id="video">
              <VideoSlide />
            </Slide>
          )}
        </Track>
      </Viewport>
    </Container>
  )
}

export default memo(CarImages)
